import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import SafeLink from "../../../components/SafeLink";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import { bdcApi } from "../../../services/bdcApi";
import { fmt$, fmtMoneyByCurrency } from "../../../utils/format";
import { fmtDateShort } from "../../../utils/fmtDate";
import { formatCommissionPct } from "../../../hooks/useCommissionConfig";

// A ledger line's settlement currency (NULL = legacy CAD row).
const lineCurrency = (l) =>
  String(l?.basis_currency || "").trim().toUpperCase() || "CAD";

const moneyByCurrency = fmtMoneyByCurrency;

const LEADERBOARD_CATEGORIES = [
  { value: "investors", label: "Investors" },
  { value: "deals",      label: "Deals" },
  { value: "subs",       label: "Sub-Contractors" },
];

const LEADERBOARD_PERIODS = [
  { value: "7d",  label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "ytd", label: "YTD" },
  { value: "all", label: "All" },
];

const COMMISSION_STATUS_TABS = [
  { value: "",           label: "All" },
  { value: "CALCULATED", label: "Calculated" },
  { value: "LOCKED",     label: "Locked" },
  { value: "APPROVED",   label: "Approved" },
  { value: "PAID",       label: "Paid" },
  { value: "FROZEN",     label: "Frozen" },
  { value: "CANCELLED",  label: "Cancelled" },
];

const STATUS_CLASS = {
  DRAFT:      "s-mute",
  CALCULATED: "s-info",
  LOCKED:     "s-rev",
  APPROVED:   "s-info",
  PAID:       "s-active",
  PAID_OUT:   "s-active",
  FROZEN:     "s-rev",
  REJECTED:   "s-fail",
  CANCELLED:  "s-mute",
};

const medal = (i) => (i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`);

function leaderboardEntityName(entity, category) {
  if (!entity) return null;
  if (category === "deals") {
    return entity.campaign_name || entity.corporation_legal_name || null;
  }
  const full = [entity.firstname, entity.lastname].filter(Boolean).join(" ");
  return full || entity.email || null;
}

function BDCPayouts() {
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState({ earned: 0, paid: 0, pending: 0 });
  const [error, setError] = useState(null);

  const [leaderCategory, setLeaderCategory] = useState("investors");
  const [leaderPeriod, setLeaderPeriod] = useState("30d");
  const [leaderRows, setLeaderRows] = useState([]);
  const [leaderLoading, setLeaderLoading] = useState(false);

  const [commissionStatus, setCommissionStatus] = useState("");
  const [commissions, setCommissions] = useState([]);
  const [commissionsTotal, setCommissionsTotal] = useState(0);
  const [commissionsLoading, setCommissionsLoading] = useState(false);

  async function refreshPayouts() {
    setLoading(true);
    try {
      const res = await bdcApi.listPayouts({ limit: 25 });
      setPayouts(res?.data?.rows || []);
      setSummary(res?.data?.summary || { earned: 0, paid: 0, pending: 0 });
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load payouts");
    } finally {
      setLoading(false);
    }
  }

  async function refreshLeaderboard() {
    setLeaderLoading(true);
    try {
      const res = await bdcApi.leaderboards({ category: leaderCategory, period: leaderPeriod });
      setLeaderRows(res?.data?.rows || []);
    } catch (_) {
      setLeaderRows([]);
    } finally {
      setLeaderLoading(false);
    }
  }

  async function refreshCommissions() {
    setCommissionsLoading(true);
    try {
      const params = { limit: 50 };
      if (commissionStatus) params.status = commissionStatus;
      const res = await bdcApi.listCommissions(params);
      setCommissions(res?.data?.rows || []);
      setCommissionsTotal(res?.data?.total || 0);
    } catch (_) {
      setCommissions([]);
      setCommissionsTotal(0);
    } finally {
      setCommissionsLoading(false);
    }
  }

  useEffect(() => { refreshPayouts(); }, []);
  useEffect(() => { refreshLeaderboard(); }, [leaderCategory, leaderPeriod]);
  useEffect(() => { refreshCommissions(); }, [commissionStatus]);

  const statusClassFor = (status) => STATUS_CLASS[String(status || "").toUpperCase()] || "s-mute";

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Payouts & analytics" />

      {error && <Alert tone="danger">{error}</Alert>}

      <section
        className="kpi-rail"
        style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
      >
        <div className="kpi kpi--neutral">
          <div className="kpi__lbl">Earned</div>
          <div className="kpi__num">{moneyByCurrency(summary.by_currency, "earned", summary.earned)}</div>
        </div>
        <div className="kpi kpi--ok">
          <div className="kpi__lbl">Paid</div>
          <div className="kpi__num">{moneyByCurrency(summary.by_currency, "paid", summary.paid)}</div>
        </div>
        <div className="kpi kpi--warn">
          <div className="kpi__lbl">Pending</div>
          <div className="kpi__num">{moneyByCurrency(summary.by_currency, "pending", summary.pending)}</div>
        </div>
      </section>

      <section className="card">
        <div className="card__head">
          <div className="card__title">Payout ledger</div>
        </div>
        <div className="card__body card__body--flush">
          {payouts.length === 0 ? (
            <Empty>No payouts recorded yet.</Empty>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Period</th>
                  <th className="r">Total</th>
                  <th>Status</th>
                  <th>Approved</th>
                  <th>Paid</th>
                  <th>Tax doc</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id}>
                    <td className="mono">{fmtDateShort(p.period_start)} → {fmtDateShort(p.period_end)}</td>
                    <td className="r">{fmt$(p.total_amount, 2, p.currency)}</td>
                    <td>
                      <span className={`tbl-status ${statusClassFor(p.status)}`}>{p.status}</span>
                    </td>
                    <td className="muted">{fmtDateShort(p.approved_at)}</td>
                    <td className="muted">{fmtDateShort(p.paid_at)}</td>
                    <td>
                      {p.tax_doc_url ? (
                        <SafeLink href={p.tax_doc_url}>{p.tax_doc_type || "Doc"}</SafeLink>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="muted">{p.payment_reference || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card__head">
          <div className="card__title">Commission ledger</div>
          <div className="card__sub mono">
            {commissionsTotal} row{commissionsTotal === 1 ? "" : "s"}
            {commissionStatus ? ` · ${commissionStatus}` : ""}
          </div>
        </div>
        <div className="card__body" style={{ paddingBottom: 0 }}>
          <div className="toolbar__pills">
            {COMMISSION_STATUS_TABS.map((t) => (
              <button
                key={t.value || "all"}
                type="button"
                className={`toolbar__pill${commissionStatus === t.value ? " toolbar__pill--on" : ""}`}
                onClick={() => setCommissionStatus(t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="card__body card__body--flush">
          {commissionsLoading ? (
            <Empty>Loading commissions…</Empty>
          ) : commissions.length === 0 ? (
            <Empty>No commission rows in this filter.</Empty>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Source</th>
                  {/* Principal-based v2 lines deliberately write net_fee = 0;
                      the honest basis is basis_amount × rate. Legacy NET_FEE
                      rows fall back to net_fee. */}
                  <th className="r">Basis</th>
                  <th className="r">Rate</th>
                  <th className="r">Commission</th>
                  <th>Payout</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((c) => {
                  const cur = lineCurrency(c);
                  const hasBasis = c.basis_amount != null;
                  return (
                    <tr key={c.id}>
                      <td>
                        <span className={`tbl-status ${statusClassFor(c.status)}`}>{c.status}</span>
                      </td>
                      <td className="muted">{c.source_type}</td>
                      <td className="r">
                        {fmt$(hasBasis ? c.basis_amount : c.net_fee, 2, cur)}
                        <span className="muted"> {hasBasis ? (c.basis_type || "") : "NET FEE"}</span>
                      </td>
                      <td className="r">{formatCommissionPct(c.commission_pct)}</td>
                      <td className="r">{fmt$(c.commission_amount, 2, cur)}</td>
                      <td className="muted">{c.payout_id ? `#${c.payout_id}` : "—"}</td>
                      <td className="muted">{fmtDateShort(c.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card__head">
          <div className="card__title">Leaderboards</div>
        </div>
        <div className="card__body">
          <div className="toolbar">
            <div className="toolbar__pills">
              {LEADERBOARD_CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  className={`toolbar__pill${leaderCategory === c.value ? " toolbar__pill--on" : ""}`}
                  onClick={() => setLeaderCategory(c.value)}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="toolbar__spacer" />
            <div className="toolbar__pills">
              {LEADERBOARD_PERIODS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  className={`toolbar__pill${leaderPeriod === p.value ? " toolbar__pill--on" : ""}`}
                  onClick={() => setLeaderPeriod(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {leaderLoading ? (
            <Empty>Loading leaderboard…</Empty>
          ) : leaderRows.length === 0 ? (
            <Empty>No commissions in this window.</Empty>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 56 }}>#</th>
                  <th>{leaderCategory === "subs" ? "Sub-contractor name" : leaderCategory === "deals" ? "Deal name" : "Investor name"}</th>
                  <th>{leaderCategory === "deals" ? "Deal ID" : leaderCategory === "subs" ? "Sub-contractor email" : "Investor email"}</th>
                  <th className="r">Lines</th>
                  <th className="r">Commission</th>
                </tr>
              </thead>
              <tbody>
                {leaderRows.map((r, i) => {
                  const name = leaderboardEntityName(r.Entity, leaderCategory);
                  const subText = leaderCategory === "deals"
                    ? `#${r.entity_id}`
                    : (r.Entity?.email || `#${r.entity_id}`);
                  return (
                    <tr key={`${r.entity_id}-${i}`}>
                      <td style={{ textAlign: "center" }}>{medal(i)}</td>
                      <td>{name || `#${r.entity_id}`}</td>
                      <td className="tbl-id">{subText}</td>
                      <td className="r mono" style={{ fontSize: "var(--t-xs)", color: "var(--tx-3)" }}>
                        {r.line_count}
                      </td>
                      <td className="r"><strong className="mono">{fmt$(r.total_commission, 2, r.currency)}</strong></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </PageShell>
  );
}

export default BDCPayouts;
